"use client";

/* eslint-disable @next/next/no-img-element */

import { useAuthorizedImageUrl } from "@/hooks/useAuthorizedImage";
import { cn } from "@/lib/utils";

export const DEFAULT_NAMESPACE_ICON = "🛠️";

/**
 * A namespace icon is either an emoji or a path to an uploaded image. Anything
 * that looks like a location is the latter — rendering it as text is how the
 * raw upload URL used to leak into the UI.
 */
export function isNamespaceIconUrl(icon: string | null | undefined): boolean {
  const value = icon?.trim() ?? "";
  return value.startsWith("/") || /^https?:\/\//i.test(value);
}

export type NamespaceIconProps = {
  icon?: string | null;
  /** Namespace name, used for the image's alt text. */
  name?: string | null;
  /** Sizing/shape for the icon box; applies to both the emoji and image forms. */
  className?: string;
};

export function NamespaceIcon({ icon, name, className }: NamespaceIconProps) {
  const value = icon?.trim() || DEFAULT_NAMESPACE_ICON;
  const isUrl = isNamespaceIconUrl(value);
  const { url, state } = useAuthorizedImageUrl(isUrl ? value : null);

  const box = cn(
    "inline-flex shrink-0 items-center justify-center overflow-hidden leading-none",
    className,
  );

  if (!isUrl || state === "failed") {
    return (
      <span className={box} aria-hidden>
        {isUrl ? DEFAULT_NAMESPACE_ICON : value}
      </span>
    );
  }

  return (
    <span className={box} aria-hidden>
      {url ? (
        <img
          src={url}
          alt={name ? `${name} icon` : "Namespace icon"}
          // contain, not cover: an uploaded logo is usually a mark with its own
          // aspect ratio, and cropping it to fill a circle cuts the brand in half.
          className="h-full w-full object-contain"
        />
      ) : null}
    </span>
  );
}
